import ga from 'react-ga4';
import { matchPath } from 'react-router-dom';
import { RouteInfo } from '../components/page-content';

export const ContentCategoryAdmin = {
  Accounts: 'admin_accounts',
  AlertConditions: 'admin_alert_conditions',
  AuthorizedApplications: 'admin_authorized_apps',
  Contracts: 'admin_contracts',
  Geofence: 'admin_geofence',
  GeofenceGroups: 'admin_geofence_groups',
  Groups: 'admin_groups',
  OIDCMapping: 'admin_oidc_mapping',
  People: 'admin_people',
  PointOfInterest: 'admin_poi',
  PointOfInterestSets: 'admin_poi_sets',
  Roles: 'admin_roles',
  Subscriptions: 'admin_subscriptions',
  Things: 'admin_things',
  Transfers: 'admin_transfers',
  UserGroups: 'admin_user_groups',
  UserProfile: 'admin_user_profile',
  Users: 'admin_users',
} as const;

export const ContentCategoryTracking = {
  Alerts: 'track_alerts',
  Analytics: 'track_analytics',
  Dashboards: 'track_dashboards',
  Map: 'track_map',
  People: 'track_people',
  Reports: 'track_reports',
  Things: 'track_things',
  UserProfile: 'track_user_profile',
} as const;

export const ContentCategoryManage = {
  Account: 'manage_account',
  Device: 'manage_device',
  DeviceDiagnostics: 'manage_device_diagnostics',
  FeatureMatrix: 'manage_feature_matrix',
  GeofenceType: 'manage_geofence_type',
  Installation: 'manage_installation',
  InstallationTemplate: 'manage_installation_template',
  People: 'manage_people',
  PersonType: 'manage_person_type',
  PointOfInterest: 'manage_poi',
  PointOfInterestSets: 'manage_poi_sets',
  PointOfInterestType: 'manage_poi_type',
  Things: 'manage_things',
  ThingType: 'manage_thing_type',
  UserProfile: 'manage_user_profile',
  UserSubscription: 'manage_user_subscription',
} as const;

export const AnalyticsEventCategory = {
  Created: 'entity_created',
  Updated: 'entity_updated',
  Viewed: 'entity_viewed',
  Exported: 'export_performed',
} as const;

type TrackingEventCategory = 'keyword_search' | 'list_pagination' | 'list' | 'micro_portal';
type EntityEventCategory = typeof AnalyticsEventCategory[keyof typeof AnalyticsEventCategory];

export type ContentCategoryAdmin = typeof ContentCategoryAdmin[keyof typeof ContentCategoryAdmin];
export type ContentCategoryTracking = typeof ContentCategoryTracking[keyof typeof ContentCategoryTracking];
export type ContentCategoryManage = typeof ContentCategoryManage[keyof typeof ContentCategoryManage];
export type ContentCategory = ContentCategoryAdmin | ContentCategoryTracking | ContentCategoryManage;

const findMatchedRoute = (routes: RouteInfo[], pathname: string): RouteInfo | undefined => {
  // Special case for /user/groups/ as the path was matching /user/:id
  if (pathname === '/user/groups') {
    return routes.find((route) => route.path === '/user/groups');
  }
  return routes.find((route) => matchPath(route.path, pathname));
};

export const trackEvent = (category: TrackingEventCategory | EntityEventCategory, action: string, feature: string, params: Record<string, string | number> = {}): void => {
  ga.gtag('event', category, {
    action,
    feature,
    ...params,
  });
};

export const trackEntityView = (entityName: string, contentCategory: ContentCategory): void => {
  trackEvent(AnalyticsEventCategory.Viewed, entityName, contentCategory);
};

export const trackEntityExport = (entityName: string, contentCategory: ContentCategory): void => {
  trackEvent(AnalyticsEventCategory.Exported, entityName, contentCategory);
};

export const trackPage = (page: string, routes: RouteInfo[]): void => {
  const matchedRoute = findMatchedRoute(routes, page);

  const data: { hitType: string; page: string; content_group?: string } = {
    hitType: 'pageview',
    page,
  };

  if (matchedRoute && matchedRoute.googleAnalyticsCategory) {
    // eslint-disable-next-line dot-notation
    data['content_group'] = matchedRoute.googleAnalyticsCategory;
  }

  ga.send(data);
};

export const setPageTitle = (title: string): void => {
  const data: Record<string, string> = {};
  // eslint-disable-next-line dot-notation
  data['page_title'] = title;
  ga.gtag('set', data);
};
